import { required, email, helpers, minLength, sameAs, numeric, minValue, maxLength } from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';

export function useRulesForForm() {
  const { t } = useI18n();
  const isCapitalLetter = helpers.regex(/[A-Z]/);
  const mustBeNumber = helpers.regex(/\d/);
  const leastOneSpecialCharacter = helpers.regex(/[^\dA-Za-z]/);
  const latinLetterPattern = helpers.regex(/^[\w!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~№-]*$/);
  const phoneValid = helpers.withParams(
    { type: 'phoneValid' },
    (value: unknown) => {
      if (value == null || value === '') return true;
      const s = String(value).trim();
      if (!s) return true;
      try {
        return isValidPhoneNumber(s);
      /* v8 ignore next 4 */
      } catch {
        return false;
      }
    },
  );
  const notSameAs = (data: { oldPassword: string; password: string }) => () => {
    return data.oldPassword !== data.password;
  };

  const RulesForFormRequired = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.required'), required),
    };
  };

  const RulesForFormEmail = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.email.required'), required),
      email: helpers.withMessage(t('form.error_for_input.email.email'), email),
    };
  };
  const RulesForFormPassword = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.password.required'), required),
      isCapitalLetter: helpers.withMessage(t('form.error_for_input.password.is_capital_letter'), isCapitalLetter),
      mustBeNumber: helpers.withMessage(t('form.error_for_input.password.must_be_number'), mustBeNumber),
      latinLetterPattern: helpers.withMessage(t('form.error_for_input.password.latin_letter_pattern'), latinLetterPattern),
      leastOneSpecialCharacter: helpers.withMessage(
        // Ключ в locales/ru.json — snake_case. Из-за camelCase гость видел под полем
        // не текст ошибки, а сырой `form.error_for_input.password.leastOneSpecialCharacter`.
        t('form.error_for_input.password.least_one_special_character'),
        leastOneSpecialCharacter,
      ),
      minLength: helpers.withMessage(t('form.error_for_input.password.min_length'), minLength(8)),
    };
  };

  /**
   * Правила пароля для формы ВХОДА: только «поле заполнено».
   *
   * Требования к сложности относятся к созданию пароля, а не к вводу существующего.
   * Раньше вход использовал те же правила, что регистрация, и гость с паролем,
   * заведённым по старым требованиям (буква + цифра, без спецсимвола), не мог войти:
   * кнопка не срабатывала, а под полем висело требование спецсимвола.
   */
  const RulesForFormPasswordLogin = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.password.required'), required),
    };
  };
  const RulesForFormConfirmPassword = (passwordInput) => {
    return {
      required: helpers.withMessage(t('form.error_for_input.confirm_password.required'), required),
      sameAs: helpers.withMessage(t('form.error_for_input.confirm_password.same_as'), sameAs(passwordInput)),
    };
  };
  const RulesForFormNewPassword = (data: { oldPassword: string; password: string }) => {
    return {
      required: helpers.withMessage(t('form.error_for_input.password.required'), required),
      isCapitalLetter: helpers.withMessage(t('form.error_for_input.password.is_capital_letter'), isCapitalLetter),
      mustBeNumber: helpers.withMessage(t('form.error_for_input.password.must_be_number'), mustBeNumber),
      latinLetterPattern: helpers.withMessage(t('form.error_for_input.password.latin_letter_pattern'), latinLetterPattern),
      leastOneSpecialCharacter: helpers.withMessage(
        t('form.error_for_input.password.least_one_special_character'),
        leastOneSpecialCharacter,
      ),
      minLength: helpers.withMessage(t('form.error_for_input.password.min-length'), minLength(8)),
      notSameAs: helpers.withMessage(t('form.error_for_input.confirm_password.not_same_as'), notSameAs(data)),
    };
  };
  const RulesForFormTwoFA = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.two_fa.required'), required),
    };
  };
  const RulesForFormCodeFromEmail = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.code_from_email.required'), required),
    };
  };
  const RulesForFormPhone = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.phone.required'), required),
      phoneValid: helpers.withMessage(t('form.error_for_input.phone.phone_pattern'), phoneValid),
    };
  };
  const RulesForFormGoals = () => {
    return {
      maxLength: helpers.withMessage(t('form.error_for_input.goals.max_length'), maxLength(500)),
    };
  };
  const RulesForNumeric = (minV) => {
    return {
      required: helpers.withMessage(t('form.error_for_input.numeric.required'), required),
      numeric: helpers.withMessage(t('form.error_for_input.numeric.pattern'), numeric),
      min: helpers.withMessage(t('form.error_for_input.numeric.min'), minValue(minV)),
    };
  };

  const RulesForFormName = () => {
    return {
      // required: helpers.withMessage(t('form.error_for_input.name.required'), required),
      // latinLetterPattern: helpers.withMessage(
      //   t('form.error_for_input.name.latin_letter_pattern'),
      //   helpers.regex(/^[\sA-Za-zА-я]+$/),
      // ),
      minLength: helpers.withMessage(t('form.error_for_input.name.min_length'), maxLength(50)),
    };
  };
  const RulesForFormLocation = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.location.required'), required),
    };
  };
  const RulesForFormMethodReceipt = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.delivery.required'), required),
    };
  };
  // eslint-disable-next-line sonarjs/no-identical-functions
  const RulesForFormDeliveryAddress = () => {
    return {
      required: helpers.withMessage(t('form.error_for_input.delivery.required'), required),
    };
  };

  return {
    RulesForFormRequired,
    RulesForFormEmail,
    RulesForFormPassword,
    RulesForFormPasswordLogin,
    RulesForFormConfirmPassword,
    RulesForFormTwoFA,
    RulesForFormCodeFromEmail,
    RulesForFormNewPassword,
    RulesForFormPhone,
    RulesForFormGoals,
    RulesForNumeric,
    RulesForFormName,
    RulesForFormLocation,
    RulesForFormMethodReceipt,
    RulesForFormDeliveryAddress,
  };
}
